import { z } from 'zod';

const PHONE = z
	.string({
		description: 'Telefon musí být textový řetězec',
		required_error: 'Telefon nesmí být prázdný',
	})
	.refine(
		value => {
			// allow optional
			if (value === undefined || value === null || value === '') {
				return true;
			}
			return /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/.test(value);
		},
		{
			message: 'Neplatný formát telefonního čísla',
		},
	);

const EMAIL = z
	.string({
		description: 'E-mail musí být textový řetězec',
		required_error: 'E-mail nesmí být prázdný',
	})

	.max(250, { message: 'E-mail nesmí být delší než 250 znaků' })
	.email({ message: 'Neplatný formát e-mailu' })
	.min(5, { message: 'E-mail nesmí být kratší než 5 znaků' });

const STREET = z
	.string({
		description: 'Ulice musí být textový řetězec',
		required_error: 'Ulice nesmí být prázdná',
	})
	.min(2, { message: 'Ulice nesmí být kratší než 2 znaky' })
	.max(100, { message: 'Ulice nesmí být delší než 100 znaků' });

const STREET_CODE = z
	.string({
		description: 'Číslo popisné musí být textový řetězec',
		required_error: 'Číslo popisné nesmí být prázdné',
	})
	.min(1, { message: 'Číslo popisné nesmí být kratší než 1 znak' })
	.max(10, { message: 'Číslo popisné nesmí být delší než 10 znaků' });

const CITY = z
	.string({
		description: 'Město musí být textový řetězec',
		required_error: 'Město nesmí být prázdné',
	})
	.min(2, { message: 'Město nesmí být kratší než 2 znaky' })
	.max(100, { message: 'Město nesmí být delší než 100 znaků' });

const POST_CODE = z
	.string({
		description: 'PSČ musí být textový řetězec',
		required_error: 'PSČ nesmí být prázdné',
	})
	.min(5, { message: 'PSČ nesmí být kratší než 5 znaků' })
	.max(10, { message: 'PSČ nesmí být delší než 10 znaků' });

const FIRST_NAME = z
	.string({
		description: 'Jméno musí být textový řetězec',
		required_error: 'Jméno nesmí být prázdné',
	})
	.min(2, { message: 'Jméno nesmí být kratší než 2 znaky' })
	.max(100, { message: 'Jméno nesmí být delší než 100 znaků' });

const LAST_NAME = z
	.string({
		description: 'Jméno musí být textový řetězec',
		required_error: 'Jméno nesmí být prázdné',
	})
	.min(2, { message: 'Příjmení nesmí být kratší než 2 znaky' })
	.max(100, { message: 'Příjmení nesmí být delší než 100 znaků' });

const BIRTH_DATE = z.date({
	description: 'Datum narození musí být validní datum',
	required_error: 'Datum narození nesmí být prázdné',
});

export const UUID_VALIDATOR = z.string().uuid();

export const DATE_VALIDATOR = z.string().regex(/^\d{1,2}\.\d{1,2}\.\d{4}$/, 'Neplatný formát (expected DD.MM.YYYY)');

export const VALIDATORS = {
	student: {
		id: {
			in: UUID_VALIDATOR,
		},
		email: {
			in: EMAIL,
		},
	},
	studentAddress: {
		id: {
			in: UUID_VALIDATOR,
		},

		street: {
			in: STREET,
		},

		streetCode: {
			in: STREET_CODE,
		},

		city: {
			in: CITY,
		},

		postCode: {
			in: POST_CODE,
		},
	},
	user: {
		email: {
			in: EMAIL,
		},
		firstName: {
			in: z
				.string({
					description: 'Jméno musí být textový řetězec',
					required_error: 'Jméno nesmí být prázdné',
				})
				.min(2, { message: 'Jméno nesmí být kratší než 2 znaky' })
				.max(100, { message: 'Jméno nesmí být delší než 100 znaků' }),
		},
		lastName: {
			in: z
				.string({
					description: 'Příjmení musí být textový řetězec',
					required_error: 'Příjmení nesmí být prázdné',
				})
				.min(2, { message: 'Příjmení nesmí být kratší než 2 znaky' })
				.max(100, { message: 'Příjmení nesmí být delší než 100 znaků' }),
		},
		phone: {
			in: PHONE.optional(),
		},
		password: {
			in: z
				.string({
					description: 'Heslo musí být textový řetězec',
					required_error: 'Heslo nesmí být prázdné',
				})
				.refine(
					value => new RegExp('^(?=.*\\W)(?=.{7,})').test(value ?? ''),
					'Neplatý formát. Heslo musí obsahovat minimálně 6 písmen a spec. znak!',
				),
		},
	},
	contactMessage: {
		email: {
			in: EMAIL,
		},
		message: {
			in: z
				.string({
					description: 'Zpráva musí být textový řetězec',
					required_error: 'Zpráva nesmí být prázdná',
				})
				.min(5, { message: 'Zpráva nesmí být kratší než 5 znaků' })
				.max(1000, { message: 'Zpráva nesmí být delší než 1000 znaků' }),
		},
	},
	report: {
		id: {
			in: UUID_VALIDATOR,
		},
	},
	school: {
		id: {
			in: UUID_VALIDATOR,
		},
	},
	studentStudyInfo: {
		note: {
			in: z
				.string({
					description: 'Poznámka musí být textový řetězec',
				})
				.max(1000, { message: 'Poznámka nesmí být delší než 1000 znaků' })
				.optional(),
		},
	},
	drivingLessonsRecord: {
		id: {
			in: UUID_VALIDATOR,
		},
		studentId: {
			in: UUID_VALIDATOR,
		},
		schoolId: {
			in: UUID_VALIDATOR,
		},
		startTime: {
			in: z.date({
				description: 'Datum a čas začátku jízdy',
				required_error: 'Datum a čas začátku jízdy nesmí být prázdný',
			}),
		},
		endTime: {
			in: z.date({
				description: 'Datum a čas začátku jízdy',
				required_error: 'Datum a čas začátku jízdy nesmí být prázdný',
			}),
		},
		length: {
			in: z
				.number({
					description: 'Délka jízdy',
					required_error: 'Délka jízdy nesmí být prázdná',
				})
				.min(1, { message: 'Délka jízdy nesmí být menší než 1' })
				.max(1440, { message: 'Délka jízdy nesmí být větší než 1440' }),
		},
		schoolCarId: {
			in: z.string().uuid().optional(),
		},
		userId: {
			in: z.string().uuid().optional(),
		},
		signature: {
			in: z.string().optional(),
		},
		tachometerBefore: {
			in: z
				.number({
					description: 'Tachometr před jízdou',
					required_error: 'Tachometr před jízdou nesmí být prázdný',
					invalid_type_error: 'Tachometr před jízdou musí být číslo',
				})
				.min(0, { message: 'Tachometr před jízdou nesmí být menší než 0' })
				.max(Number.MAX_SAFE_INTEGER, {
					message: `Tachometr před jízdou nesmí být větší než ${Number.MAX_SAFE_INTEGER}`,
				})
				.nullable()
				.optional(),
		},
		tachometerAfter: {
			in: z
				.number({
					description: 'Tachometr před jízdou',
					required_error: 'Tachometr před jízdou nesmí být prázdný',
					invalid_type_error: 'Tachometr po jízdě musí být číslo',
				})
				.min(0, { message: 'Tachometr před jízdou nesmí být menší než 0' })
				.max(Number.MAX_SAFE_INTEGER, {
					message: `Tachometr před jízdou nesmí být větší než ${Number.MAX_SAFE_INTEGER}`,
				})
				.nullable()
				.optional(),
		},
	},
	schoolInvite: {
		id: {
			in: UUID_VALIDATOR,
		},
	},
	paymentItem: {
		id: {
			in: UUID_VALIDATOR,
		},
		note: {
			in: z
				.string({
					description: 'Poznámka k platbě',
				})
				.max(1000, { message: 'Poznámka k platbě nesmí být delší než 1000 znaků' })
				.optional(),
		},
		price: {
			in: z
				.number({
					description: 'Částka platby',
					required_error: 'Částka platby nesmí být prázdná',
					invalid_type_error: 'Částka platby musí být číslo',
				})
				.min(0, { message: 'Částka nesmí být menší než 0' })
				.max(1_000_000, {
					message: `Částka nesmí být větší než 1 000 000`,
				}),
		},
		studentId: {
			in: UUID_VALIDATOR,
		},
		schoolId: {
			in: UUID_VALIDATOR,
		},
	},
	applicationForm: {
		id: {
			in: UUID_VALIDATOR,
		},
		name: {
			in: z
				.string({
					description: 'Název formuláře',
					required_error: 'Název nesmí být prázdný',
					invalid_type_error: 'Název musí být textový řetězec',
				})
				.max(256, { message: 'Název nesmí být delší než 256 znaků' }),
		},
		colorPrimary: {
			in: z
				.string({
					description: 'Hlavní barva formuláře',
					required_error: 'Barva nesmí být prázdná',
					invalid_type_error: 'Barva musí být textový řetězec',
				})
				.max(256, { message: 'Barva nesmí být delší než 10 znaků' }),
		},
		colorError: {
			in: z
				.string({
					description: 'Hlavní barva formuláře',
					required_error: 'Barva nesmí být prázdná',
					invalid_type_error: 'Barva musí být textový řetězec',
				})
				.max(256, { message: 'Barva nesmí být delší než 256 znaků' }),
		},
		origins: {
			in: z
				.array(
					z
						.string({
							description: 'Doména formuláře',
							required_error: 'Doména nesmí být prázdná',
							invalid_type_error: 'Doména musí být textový řetězec',
						})
						// .min(1, { message: 'Doména musí být delší víc než 1 znak' })
						.max(1024, { message: 'Doména nesmí být delší než 1024 znaků' }),
				)
				.min(1, { message: 'Musí být alespoň jedna doména' }),
		},
	},
	applicationFormCourse: {
		id: {
			in: UUID_VALIDATOR,
		},
		name: {
			in: z
				.string({
					description: 'Název kurzu',
					required_error: 'Název nesmí být prázdný',
					invalid_type_error: 'Název musí být textový řetězec',
				})
				.min(1, { message: 'Název nesmí být kratší než 1 znak' })
				.max(256, { message: 'Název nesmí být delší než 256 znaků' }),
		},
		description: {
			in: z
				.string({
					description: 'Popis kurzu',
					required_error: 'Popis nesmí být prázdný',
					invalid_type_error: 'Popis musí být textový řetězec',
				})
				.min(1, { message: 'Popis nesmí být kratší než 1 znak' })
				.max(1024, { message: 'Popis nesmí být delší než 1024 znaků' }),
		},
		price: {
			in: z
				.number({
					description: 'Cena kurzu',
					required_error: 'Cena nesmí být prázdná',
					invalid_type_error: 'Cena musí být číslo',
				})
				.min(1, { message: 'Cena nesmí být menší než 1' })
				.max(1_000_000, { message: 'Cena nesmí být větší než 1 000 000' }),
		},
	},
	schoolOperator: {
		name: {
			in: z
				.string({
					description: 'Jméno',
					required_error: 'Jméno nesmí být prázdné',
					invalid_type_error: 'Jméno musí být textový řetězec',
				})
				.min(1, { message: 'Jméno nesmí být kratší než 1 znak' })
				.max(1000, { message: 'Jméno nesmí být delší než 1000 znaků' }),
		},
		companyNumber: {
			in: z
				.string({
					description: 'IČO',
					required_error: 'IČO nesmí být prázdné',
					invalid_type_error: 'IČO musí být textový řetězec',
				})
				.min(1, { message: 'IČO nesmí být kratší než 1 znak' })
				.max(30, { message: 'IČO nesmí být delší než 30 znaků' }),
		},
		street: {
			in: STREET,
		},
		streetCode: {
			in: STREET_CODE,
		},
		city: {
			in: CITY,
		},
		postCode: { in: POST_CODE },
	},
	studentCode: {
		secretCode: {
			in: z
				.string({
					description: 'Tajný kód',
					required_error: 'Tajný kód nesmý být prázdný',
					invalid_type_error: 'Tajný kód musí být textový řetězec',
				})
				.min(8, { message: 'Tajný kód nesmí být kratší než 8 znak' })
				.max(8, { message: 'Tajný kód nesmí být delší než 8 znaků' }),
		},
	},
	interpreterInfo: {
		firstName: { in: FIRST_NAME },
		lastName: { in: LAST_NAME },
		degree: { in: z.string().optional() },
		birthDate: { in: BIRTH_DATE },
		phone: { in: PHONE },
	},
	interpreterAddress: {
		street: { in: STREET },
		streetCode: { in: STREET_CODE },
		city: { in: CITY },
		postCode: { in: POST_CODE },
	},
};

export const USER_VALIDATORS = VALIDATORS.user;
export const CONTACT_MESSAGE_VALIDATORS = VALIDATORS.contactMessage;
export const REPORT_VALIDATORS = VALIDATORS.report;
export const SCHOOL_VALIDATORS = VALIDATORS.school;
export const DRIVING_LESSONS_RECORD_VALIDATORS = VALIDATORS.drivingLessonsRecord;
export const SCHOOL_INVITE_VALIDATORS = VALIDATORS.schoolInvite;
export const STUDENT_VALIDATORS = VALIDATORS.student;
export const PAYMENT_ITEM_VALIDATORS = VALIDATORS.paymentItem;
export const APPLICATION_FORM_VALIDATORS = VALIDATORS.applicationForm;
export const APPLICATION_FORM_COURSE_VALIDATORS = VALIDATORS.applicationFormCourse;
export const STUDENT_ADDRESS_VALIDATORS = VALIDATORS.studentAddress;
export const STUDENT_STUDY_INFO_VALIDATORS = VALIDATORS.studentStudyInfo;
export const SCHOOL_OPERATOR_VALIDATORS = VALIDATORS.schoolOperator;
export const STUDENT_CODE_VALIDATORS = VALIDATORS.studentCode;
export const INTERPRETER_INFO_VALIDATORS = VALIDATORS.interpreterInfo;
export const INTERPRETER_ADDRESS_VALIDATORS = VALIDATORS.interpreterAddress;
